import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  DoorOpen, Upload, Trash2, Loader2, Crosshair, FlaskConical, Check, X, ImagePlus, ScanSearch,
} from 'lucide-react';
import HUDPageHeader from '../../components/HUDPageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'door', label: 'Porte simple' },
  { value: 'double_door', label: 'Porte double' },
  { value: 'sliding_door', label: 'Porte coulissante' },
  { value: 'window', label: 'Fenêtre' },
  { value: 'french_window', label: 'Porte-fenêtre' },
  { value: 'bay_window', label: 'Baie vitrée' },
  { value: 'garage_door', label: 'Porte de garage' },
  { value: 'other', label: 'Autre' },
];
const typeLabel = (v) => TYPE_OPTIONS.find(t => t.value === v)?.label || v;

// ---------------------------------------------------------------------
// Éditeur visuel de points d'ancrage (bonus UX Chantier 1)
// Clic sur l'image → pose l'ancre active (gauche puis droite).
// ---------------------------------------------------------------------
const AnchorEditor = ({ picto, onClose, onSaved }) => {
  const [anchors, setAnchors] = useState({
    left: picto.anchor_points?.left || null,
    right: picto.anchor_points?.right || null,
  });
  const [activeSide, setActiveSide] = useState('left');
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  const handleClick = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setAnchors(a => ({ ...a, [activeSide]: { x, y } }));
    if (activeSide === 'left') setActiveSide('right');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pictograms/${picto.pictogram_id}`, {
        anchor_points: anchors,
      }, { withCredentials: true });
      toast.success('Points d\u2019ancrage enregistrés');
      onSaved(anchors);
      onClose();
    } catch {
      toast.error('Échec de l\u2019enregistrement');
    } finally { setSaving(false); }
  };

  const Marker = ({ pt, color, label }) => pt && (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%` }}
    >
      <div className={`w-4 h-4 rounded-full border-2 border-white shadow-lg ${color}`} />
      <span className="absolute top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white bg-black/70 px-1 rounded whitespace-nowrap">{label}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!z-[2200] max-w-2xl bg-[#0a0a0c] border-white/10 text-white" data-testid="anchor-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Crosshair className="w-4 h-4 text-[#D9A05B]" />
            Points d'ancrage — {picto.name}
          </DialogTitle>
          <DialogDescription className="text-white/45 text-xs">
            Cliquez sur l'image là où la menuiserie touche le mur (gauche puis droite).
            Ces ancres serviront au raycasting de la signature du mur (Chantier 3).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          {['left', 'right'].map(side => (
            <button
              key={side}
              onClick={() => setActiveSide(side)}
              className={`px-3 h-8 rounded-lg border text-xs font-semibold transition-colors ${
                activeSide === side
                  ? (side === 'left' ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-sky-500 border-sky-500 text-white')
                  : 'bg-white/5 border-white/15 text-white/55 hover:text-white'
              }`}
              data-testid={`anchor-side-${side}`}
            >
              {anchors[side] && <Check className="w-3 h-3 inline mr-1" />}
              Ancre {side === 'left' ? 'gauche' : 'droite'}
            </button>
          ))}
          <button
            onClick={() => setAnchors({ left: null, right: null })}
            className="ml-auto text-[11px] text-white/45 hover:text-white underline underline-offset-2"
            data-testid="anchor-reset-btn"
          >
            Réinitialiser
          </button>
        </div>

        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-white cursor-crosshair select-none">
          <img
            ref={imgRef}
            src={`${API}/admin/pictograms/${picto.pictogram_id}/image`}
            alt={picto.name}
            onClick={handleClick}
            className="w-full max-h-[420px] object-contain"
            draggable={false}
            data-testid="anchor-editor-image"
          />
          <Marker pt={anchors.left} color="bg-emerald-500" label="Gauche" />
          <Marker pt={anchors.right} color="bg-sky-500" label="Droite" />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/15 text-white hover:bg-white/10 h-9">
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={saving || (!anchors.left && !anchors.right)}
            className="bg-[#D9A05B] hover:bg-[#C78D46] text-black h-9"
            data-testid="anchor-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer les ancres'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// Formulaire d'upload
// ---------------------------------------------------------------------
const UploadForm = ({ onCreated }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('door');
  const [widthCm, setWidthCm] = useState('73');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f || !f.type.startsWith('image/')) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (!name) setName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
  };

  const submit = async () => {
    if (!file || !name.trim() || !parseFloat(widthCm)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      fd.append('type', type);
      fd.append('standard_width_cm', widthCm);
      const r = await axios.post(`${API}/admin/pictograms`, fd, { withCredentials: true });
      toast.success(`Pictogramme "${r.data.name}" ajouté`);
      setFile(null); setPreview(null); setName(''); setWidthCm('73');
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\u2019upload');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="picto-upload-form">
      <p className="text-sm font-semibold text-white flex items-center gap-2">
        <ImagePlus className="w-4 h-4 text-[#D9A05B]" />
        Ajouter un pictogramme
      </p>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
        className="border-2 border-dashed border-white/15 hover:border-[#D9A05B]/50 rounded-xl p-4 text-center cursor-pointer transition-colors bg-black/20"
        data-testid="picto-dropzone"
      >
        {preview ? (
          <img src={preview} alt="aperçu" className="max-h-28 mx-auto rounded bg-white p-1" />
        ) : (
          <div className="text-white/40 text-xs py-4">
            <Upload className="w-6 h-6 mx-auto mb-2 opacity-60" />
            Glissez un screenshot du symbole ici, ou cliquez pour choisir
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])}
          data-testid="picto-file-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Nom</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Porte int. battante 73"
            className="bg-white/5 border-white/15 text-white h-9" data-testid="picto-name-input" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Catégorie</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full h-9 bg-white/5 border border-white/15 rounded-md text-white text-sm px-2 focus:outline-none focus:border-[#D9A05B]/50 [&>option]:bg-[#0a0a0c]"
            data-testid="picto-type-select"
          >
            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Largeur réelle (cm)</label>
          <div className="flex gap-1.5">
            <Input type="number" min="1" max="1000" value={widthCm} onChange={(e) => setWidthCm(e.target.value)}
              className="bg-white/5 border-white/15 text-white h-9" data-testid="picto-width-input" />
          </div>
          <div className="flex gap-1 mt-1.5">
            {[63, 73, 83, 93].map(w => (
              <button key={w} onClick={() => setWidthCm(String(w))}
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${widthCm === String(w) ? 'bg-[#D9A05B] text-black border-[#D9A05B]' : 'bg-white/5 text-white/45 border-white/10 hover:text-white'}`}
                data-testid={`picto-width-preset-${w}`}>
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={busy || !file || !name.trim() || !parseFloat(widthCm)}
        className="w-full bg-[#D9A05B] hover:bg-[#C78D46] text-black h-10"
        data-testid="picto-submit-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Enregistrer le pictogramme</>}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------
// [Chantier 2] Sandbox — Tester le matching sur un plan
// ---------------------------------------------------------------------
const MatchingTester = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [threshold, setThreshold] = useState(0.62);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const run = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('threshold', String(threshold));
      const r = await axios.post(`${API}/admin/pictograms/test-matching`, fd, {
        withCredentials: true, timeout: 120000,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du matching');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!z-[2200] max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0c] border-white/10 text-white" data-testid="matching-tester-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <ScanSearch className="w-4 h-4 text-[#D9A05B]" />
            Tester le matching — Sandbox Chantier 2
          </DialogTitle>
          <DialogDescription className="text-white/45 text-xs">
            PDF converti à 300 DPI · rotations 0/90/180/270° · multi-échelles · auto-scaling
            par largeur médiane des menuiseries détectées.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3 flex-wrap">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
            className="flex-1 min-w-[220px] border-2 border-dashed border-white/15 hover:border-[#D9A05B]/50 rounded-xl px-4 py-3 cursor-pointer transition-colors bg-black/20 text-xs text-white/50"
            data-testid="matching-dropzone"
          >
            {file ? <span className="text-white">{file.name}</span> : 'Glissez un plan (PDF ou image) ou cliquez'}
            <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="matching-file-input" />
          </div>
          <div className="w-44">
            <label className="block text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">
              Seuil : {Math.round(threshold * 100)}%
            </label>
            <input type="range" min="0.45" max="0.9" step="0.01" value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full accent-[#D9A05B]" data-testid="matching-threshold-slider" />
          </div>
          <Button onClick={run} disabled={busy || !file}
            className="bg-[#D9A05B] hover:bg-[#C78D46] text-black h-10" data-testid="matching-run-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Analyse…</> : <><ScanSearch className="w-4 h-4 mr-1.5" /> Lancer</>}
          </Button>
        </div>

        {result && (
          <div className="space-y-4" data-testid="matching-results">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-lg bg-white/8 border border-white/10 text-xs font-semibold" data-testid="matching-count">
                {result.detections_count} détection{result.detections_count > 1 ? 's' : ''}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-white/8 border border-white/10 text-xs text-white/55">
                {result.elapsed_s}s
              </span>
              {Object.values(result.per_pictogram || {}).map((p, i) => (
                <span key={i} className="px-2.5 py-1 rounded-lg bg-[#D9A05B]/12 border border-[#D9A05B]/30 text-xs text-[#D9A05B]">
                  {p.name} × {p.count}
                </span>
              ))}
            </div>

            {result.scale ? (
              <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-4" data-testid="matching-scale">
                <p className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-2">
                  ✨ Mise à l'échelle magique — à valider
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div>
                    <p className="text-lg font-bold text-white">{result.scale.cm_per_px} cm</p>
                    <p className="text-[10px] text-white/45 uppercase tracking-wider">par pixel (300 DPI)</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">{result.scale.px_per_m} px</p>
                    <p className="text-[10px] text-white/45 uppercase tracking-wider">par mètre</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">{result.scale.approx_paper_scale || '—'}</p>
                    <p className="text-[10px] text-white/45 uppercase tracking-wider">échelle papier ≈</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">{result.scale.samples}</p>
                    <p className="text-[10px] text-white/45 uppercase tracking-wider">échantillons</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-orange-400">Aucune détection — baissez le seuil ou enrichissez la bibliothèque.</p>
            )}

            {result.overlay_jpg_b64 && (
              <div className="rounded-xl overflow-hidden border border-white/10 bg-white">
                <img src={`data:image/jpeg;base64,${result.overlay_jpg_b64}`} alt="Détections"
                  className="w-full object-contain" data-testid="matching-overlay-img" />
              </div>
            )}

            {/* [Chantier 3] Signature du mur + murs extraits */}
            {result.wall_analysis && (
              <div className="space-y-3" data-testid="wall-analysis">
                <div className="bg-[#D9A05B]/8 border border-[#D9A05B]/25 rounded-xl p-4">
                  <p className="text-xs font-bold text-[#D9A05B] uppercase tracking-widest mb-2">
                    🧬 Chantier 3 — Signature du mur (zero-shot)
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">
                        {{ double_line: 'Double ligne', solid: 'Aplat plein', hatched: 'Hachures' }[result.wall_analysis.signature.pattern] || result.wall_analysis.signature.pattern}
                      </p>
                      <p className="text-[10px] text-white/45 uppercase tracking-wider">motif appris</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{result.wall_analysis.signature.thickness_px} px</p>
                      <p className="text-[10px] text-white/45 uppercase tracking-wider">épaisseur mur</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{result.wall_analysis.signature.rays_ok}</p>
                      <p className="text-[10px] text-white/45 uppercase tracking-wider">rayons réussis</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{result.wall_analysis.stats.wall_coverage_pct}%</p>
                      <p className="text-[10px] text-white/45 uppercase tracking-wider">couverture murs</p>
                    </div>
                  </div>
                </div>
                {result.walls_jpg_b64 && (
                  <div className="rounded-xl overflow-hidden border border-[#D9A05B]/30 bg-white">
                    <img src={`data:image/jpeg;base64,${result.walls_jpg_b64}`} alt="Murs extraits"
                      className="w-full object-contain" data-testid="walls-overlay-img" />
                  </div>
                )}
                <p className="text-[10.5px] text-white/40 leading-relaxed">
                  <b className="text-[#D9A05B]">Or</b> = murs propagés depuis les ancres ·
                  <b className="text-cyan-400"> cyan</b> = rayons de lecture ·
                  <b className="text-red-400"> rouge</b> = points d'impact (seeds).
                  Textes, cotes et mobilier sont ignorés car ils ne portent pas la signature.
                </p>
              </div>
            )}
            {result.detections_count > 0 && !result.wall_analysis && (
              <p className="text-xs text-orange-400">
                Signature du mur indisponible — définissez les points d'ancrage de vos pictogrammes (bouton "Ancres").
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// Page principale
// ---------------------------------------------------------------------
export default function PictogramsLabPage() {
  const [pictos, setPictos] = useState(null);
  const [editingAnchors, setEditingAnchors] = useState(null);
  const [testerOpen, setTesterOpen] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/pictograms`, { withCredentials: true })
      .then(r => setPictos(r.data))
      .catch(() => toast.error('Impossible de charger la bibliothèque'));
  };
  useEffect(load, []);

  const toggleEnabled = async (p) => {
    try {
      await axios.put(`${API}/admin/pictograms/${p.pictogram_id}`, { enabled: !p.enabled }, { withCredentials: true });
      setPictos(ps => ps.map(x => x.pictogram_id === p.pictogram_id ? { ...x, enabled: !p.enabled } : x));
    } catch { toast.error('Échec'); }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/admin/pictograms/${p.pictogram_id}`, { withCredentials: true });
      setPictos(ps => ps.filter(x => x.pictogram_id !== p.pictogram_id));
      toast.success('Pictogramme supprimé');
    } catch { toast.error('Échec de la suppression'); }
  };

  return (
    <div data-hud-page data-testid="pictograms-lab-page" className="fade-in">
      <HUDPageHeader
        module="Lab"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Pictogrammes' }]}
        icon={DoorOpen}
        title="Bibliothèque de Pictogrammes"
        subtitle="Base de connaissance menuiseries — pipeline Door-First Vectorization"
        actions={(
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTesterOpen(true)}
              className="hud-btn hud-btn-secondary hud-btn-md"
              data-testid="open-matching-tester-btn"
            >
              <ScanSearch className="w-4 h-4" /> Tester le matching
            </button>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] uppercase tracking-widest font-bold bg-orange-500/15 text-orange-400 border border-orange-500/40">
              <FlaskConical className="w-3.5 h-3.5" /> Module Lab
            </span>
          </div>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne upload */}
        <div className="lg:col-span-1">
          <UploadForm onCreated={(p) => { setPictos(ps => [p, ...(ps || [])]); setEditingAnchors(p); }} />
          <p className="text-[10.5px] text-white/35 mt-3 leading-relaxed px-1">
            Après l'upload, définissez les <b className="text-white/60">points d'ancrage</b> (contact
            porte ↔ mur) : ils alimentent l'auto-scaling (Chantier 2) et le raycasting de la
            signature du mur (Chantier 3).
          </p>
        </div>

        {/* Colonne bibliothèque */}
        <div className="lg:col-span-2">
          {!pictos ? (
            <div className="flex items-center gap-2 text-white/40 text-sm py-12 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : pictos.length === 0 ? (
            <div className="text-center py-16 text-white/35 text-sm border border-dashed border-white/10 rounded-2xl">
              <DoorOpen className="w-8 h-8 mx-auto mb-3 opacity-40" />
              Bibliothèque vide — ajoutez votre premier symbole de porte.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {pictos.map(p => (
                <div key={p.pictogram_id}
                  className={`rounded-xl border overflow-hidden transition-opacity ${p.enabled ? 'border-white/10 bg-white/[0.03]' : 'border-white/5 bg-white/[0.01] opacity-50'}`}
                  data-testid={`picto-card-${p.pictogram_id}`}>
                  <div className="h-28 bg-white flex items-center justify-center p-2">
                    <img src={`${API}/admin/pictograms/${p.pictogram_id}/image`} alt={p.name} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="p-3 space-y-1.5">
                    <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#D9A05B]/15 text-[#D9A05B] border border-[#D9A05B]/30">{typeLabel(p.type)}</span>
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-white/8 text-white/55 border border-white/10">{p.standard_width_cm} cm</span>
                      {(p.anchor_points?.left || p.anchor_points?.right) ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Ancres ✓</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/30">Sans ancres</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <button onClick={() => setEditingAnchors(p)}
                        className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md bg-white/5 border border-white/10 text-[11px] text-white/70 hover:text-white hover:bg-white/10"
                        data-testid={`picto-anchors-btn-${p.pictogram_id}`}>
                        <Crosshair className="w-3 h-3" /> Ancres
                      </button>
                      <button onClick={() => toggleEnabled(p)}
                        title={p.enabled ? 'Désactiver (ignoré par le matching)' : 'Activer'}
                        className={`w-7 h-7 rounded-md border flex items-center justify-center ${p.enabled ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' : 'bg-white/5 border-white/10 text-white/35'}`}
                        data-testid={`picto-toggle-${p.pictogram_id}`}>
                        {p.enabled ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => remove(p)}
                        className="w-7 h-7 rounded-md bg-white/5 border border-white/10 text-white/35 hover:text-red-400 flex items-center justify-center"
                        data-testid={`picto-delete-${p.pictogram_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editingAnchors && (
        <AnchorEditor
          picto={editingAnchors}
          onClose={() => setEditingAnchors(null)}
          onSaved={(anchors) => setPictos(ps => ps.map(x => x.pictogram_id === editingAnchors.pictogram_id ? { ...x, anchor_points: anchors } : x))}
        />
      )}
      {testerOpen && <MatchingTester onClose={() => setTesterOpen(false)} />}
    </div>
  );
}
